/**
 * /join — accept-invite page (#495), reachable logged in or out.
 *
 * The URL carries the single-use token plus display hints
 * (workspace/role/inviter/email) from the invite email; only the token
 * is authoritative — POST /auth/v1/invites/accept enforces everything.
 *
 * Logged in: one click (or automatically, when arriving back from the
 * signup/login round-trip) → accept → switch into the tenant → land in
 * the workspace. Logged out: the invite is parked in localStorage,
 * signup is pre-filled with the invited email, and the accept happens
 * on the first authenticated visit — no orphan personal-only session.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Users } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { getAuthAdapter } from '@puglieseweb/shared-react-auth';
import { useAuthContext } from '@/contexts/AuthContext';
import { errorStatus, problemDetail, tenantApi } from '@/services/tenantApi';
import {
  clearPendingInvite,
  loadPendingInvite,
  savePendingInvite,
  type PendingInvite,
} from '@/utils/pendingInvite';

type Phase = 'ready' | 'accepting' | 'joined' | 'error';

function friendlyError(err: unknown): string {
  const status = errorStatus(err);
  if (status === 404) {
    return (
      'This invite is not valid for this account. It may have been sent to a ' +
      'different email address, been revoked, or already used — ask your ' +
      'workspace admin to send a new invite.'
    );
  }
  if (status === 409) {
    return problemDetail(err, 'This invite can no longer be used.') +
      ' Ask your workspace admin to send a new invite.';
  }
  return problemDetail(err, 'Could not accept the invite. Please try again.');
}

export default function JoinPage() {
  const { isAuthenticated, isLoading } = useAuthContext();
  const [searchParams] = useSearchParams();

  // URL params win; the localStorage handoff covers the auth round-trip.
  const invite = useMemo<PendingInvite | null>(() => {
    const token = searchParams.get('token');
    if (token) {
      return {
        token,
        email: searchParams.get('email') ?? undefined,
        workspace: searchParams.get('workspace') ?? undefined,
        role: searchParams.get('role') ?? undefined,
        inviter: searchParams.get('inviter') ?? undefined,
      };
    }
    return loadPendingInvite();
  }, [searchParams]);

  // A stored invite means the user came back through signup/login with
  // intent to join — accept without another click.
  const cameFromAuthFlow = useRef(loadPendingInvite() !== null);

  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<string | null>(null);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);

  const accept = useCallback(async () => {
    if (!invite) return;
    setPhase('accepting');
    setError(null);
    try {
      const result = await tenantApi.acceptInvite(invite.token);
      clearPendingInvite();
      setWorkspaceName(result.displayName ?? invite.workspace ?? null);
      setPhase('joined');
      // Land inside the new workspace: flip the active tenant, mint the
      // new claims, and let the app's subdomain redirect do the rest.
      await tenantApi.switchTenant(result.tenantId);
      try {
        await getAuthAdapter().fetchSession({ forceRefresh: true });
      } catch {
        // Non-fatal — the reload re-authenticates from cookies.
      }
      window.location.assign('/');
    } catch (err) {
      clearPendingInvite();
      if (errorStatus(err) === 409 &&
          problemDetail(err, '').toLowerCase().includes('already a member')) {
        setAlreadyMember(true);
        setPhase('error');
        return;
      }
      setError(friendlyError(err));
      setPhase('error');
    }
  }, [invite]);

  // Park the invite while logged out so it survives signup/login.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && invite) savePendingInvite(invite);
  }, [isLoading, isAuthenticated, invite]);

  // Auto-accept when arriving back from the auth round-trip.
  useEffect(() => {
    if (!isLoading && isAuthenticated && invite && cameFromAuthFlow.current && phase === 'ready') {
      cameFromAuthFlow.current = false;
      accept();
    }
  }, [isLoading, isAuthenticated, invite, phase, accept]);

  const workspaceLabel = invite?.workspace || 'a workspace';
  const signupHref = invite?.email
    ? `/signup?email=${encodeURIComponent(invite.email)}`
    : '/signup';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center space-y-5">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
          <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        </div>

        {!invite ? (
          <>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              Invite link missing
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              This page needs an invite link. Open the link from your invitation email, or ask
              your workspace admin to send a new invite.
            </p>
            <Link to="/" className="inline-block text-sm font-medium text-purple-600 hover:underline">
              Go to SystemDox
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              Join {workspaceLabel}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {invite.inviter ? `${invite.inviter} invited you` : 'You have been invited'}
              {invite.role ? (
                <>
                  {' '}to join as <span className="font-medium">{invite.role}</span>
                </>
              ) : null}
              .
            </p>

            {isLoading || phase === 'accepting' ? (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Spinner size="sm" />
                {phase === 'accepting' ? 'Joining workspace…' : 'Loading…'}
              </div>
            ) : phase === 'joined' ? (
              <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <Check className="w-4 h-4" />
                Joined{workspaceName ? ` ${workspaceName}` : ''} — taking you there…
              </div>
            ) : phase === 'error' && alreadyMember ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  You're already a member of this workspace.
                </p>
                <Link
                  to="/"
                  className="inline-block rounded-md bg-purple-600 px-5 py-2 text-sm font-medium text-white hover:bg-purple-700"
                >
                  Open SystemDox
                </Link>
              </>
            ) : phase === 'error' ? (
              <>
                <p className="flex items-start gap-2 text-left text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" /> {error}
                </p>
                <Link to="/" className="inline-block text-sm font-medium text-purple-600 hover:underline">
                  Go to SystemDox
                </Link>
              </>
            ) : isAuthenticated ? (
              <button
                type="button"
                onClick={accept}
                className="rounded-md bg-purple-600 px-5 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                Join workspace
              </button>
            ) : (
              <div className="space-y-3">
                <a
                  href={signupHref}
                  className="block rounded-md bg-purple-600 px-5 py-2 text-sm font-medium text-white hover:bg-purple-700"
                >
                  Create an account to join
                </a>
                <a
                  href="/login"
                  className="block text-sm font-medium text-purple-600 dark:text-purple-400 hover:underline"
                >
                  Already have an account? Sign in
                </a>
                {invite.email && (
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Use <span className="font-medium">{invite.email}</span> — the invite is tied
                    to that address.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
